import UrlPattern from 'url-pattern';

import { autobind } from 'core-decorators';

import eventEmitter from '@resn/gozer-decorators/event-emitter-decorator';

@eventEmitter
class RouterSingleton {
    NAVIGATE = 'ROUTER_NAVIGATE';

    config = {
        // update URL on navigation
        pushState: true,
        // initial redirect
        redirect: null,
    };

    history = [];
    patterns = [];
    matchedRoute = undefined;
    root = '';
    pathname = '/';

    init(routes, config = {}) {
        this.config = Object.assign(this.config, config);

        if (this.config.pushState) {
            this.addListeners();
        }

        this.parseRoutes(routes);

        if (
            this.match(location.pathname).match.options &&
            this.match(location.pathname).match.options.deeplink !== undefined &&
            !this.match(location.pathname).match.options.deeplink
        ) {
            this.changeURLSilently('/');
        }

        // add current route to history
        this.history.push(config.redirect || location.pathname);

        // dispatch first navigation event
        this.onNavigate(config.redirect || location.pathname, true);
    }

    /**
     * Listen to browser navigation changes
     */

    addListeners() {
        const onNavigate = this.onNavigate;

        addEventListener('popstate', () => {
            onNavigate(location.pathname, false, true);
        });

        ['push', 'replace'].map((type) => {
            const state = `${type}State`;
            const historyState = history[state];

            history[state] = function () {
                if (!arguments[0].silent) {
                    onNavigate(arguments[2]);
                }

                return historyState.apply(history, arguments);
            };
        });
    }

    /**
     * Create URL patterns based on routes configuration.
     * @param {array} routes array of valid routes patterns
     */

    parseRoutes(routes) {
        this.patterns = Object.values(routes).map(({ path, ...args }) => {
            const res = {
                pattern: new UrlPattern(this.root + path),
                path,
                ...args,
            };

            return res;
        });
    }

    /**
     * Find a matching pathname pattern in the routes array
     * @param {string} url pathname
     */

    match(url) {
        const match = this.findPattern(url);

        if (match) {
            return {
                ...match,
            };
        }

        return false;
    }

    findPattern(url) {
        let params;

        return {
            match: this.patterns.find((pattern) => {
                params = pattern.pattern.match(url);
                return !!params;
            }),
            params,
        };
    }

    /**
     * Navigate to specific pathname (e.g. /landing) and adds to history array.
     * If pushState is disabled the router will internally navigate
     * but the URL will be kept the same.
     * @param {string} to target path name
     */

    navigate(to) {
        let route = to;

        this.history.push(route);

        if (this.config.pushState) {
            history.pushState({}, null, route);
        } else {
            this.onNavigate(route);
        }
    }

    /**
     * Replace current routing state with new pathname.
     * If pushState is disabled the router will internally navigate but
     * but the URL will be kept the same.
     * @param {string} route target path name
     */

    replace(route) {
        this.history[this.history.length - 1] = route;

        if (this.config.pushState) {
            history.replaceState({}, null, route);
        } else {
            this.onNavigate(route);
        }
    }

    /**
     * Update browser URL without dispatching push or replace state updates.
     * @param {string} route target pathname
     */

    changeURLSilently(route) {
        history.replaceState({ silent: true }, null, route);
    }

    /**
     * Called when a router navigation is detected
     * @param {proxy} route
     * @param {boolean} firstTime
     */

    @autobind
    onNavigate(route, firstTime = false, popstate = false) {
        this.pathname = route;

        const { match, params } = this.matchedRoute || {};
        this.matchedRoute = { ...this.match(route), pathname: this.pathname, previous: { match, params } };

        this.emit(this.NAVIGATE, this.matchedRoute, firstTime, popstate);
    }

    /**
     * Update page's metadata
     *
     * @param {string} title
     * @param {string} description
     */
    updateMeta(metadata) {
        if (metadata.title && document.title !== metadata.title) {
            document.title = metadata.title;
        }

        if (metadata.description && document.querySelector('meta[name="description"]')) {
            document.querySelector('meta[name="description"]').setAttribute('content', metadata.description);
        }
    }
}

const Router = new RouterSingleton();

export { Router };
